/**
 * useSpeechRecognition — React hook wrapping the Web Speech API.
 *
 * Provides push-to-talk voice input with real-time interim transcripts.
 * Falls back gracefully when the browser doesn't support the API (Firefox, etc.).
 *
 * Usage:
 *   const { start, stop, transcript, interimTranscript, listening, supported, error } = useSpeechRecognition();
 */
import { useCallback, useEffect, useRef, useState } from 'react';

// Vendor-prefixed typings — Chrome ships under `webkitSpeechRecognition`.
type SpeechRecognitionType = typeof window extends { SpeechRecognition: infer S } ? S : any;

function getSpeechRecognition(): SpeechRecognitionType | null {
  if (typeof window === 'undefined') return null;
  return (window as any).SpeechRecognition ?? (window as any).webkitSpeechRecognition ?? null;
}

export type VoiceStatus = 'idle' | 'listening' | 'processing';

interface UseSpeechRecognitionReturn {
  /** Start listening. No-op if already active or unsupported. */
  start: () => void;
  /** Stop listening. Fires final result if available. */
  stop: () => void;
  /** Toggle listening on/off. */
  toggle: () => void;
  /** Final committed transcript for the current session. */
  transcript: string;
  /** Real-time interim transcript (updates while speaking). */
  interimTranscript: string;
  /** Current status. */
  status: VoiceStatus;
  /** Convenience: true when status === 'listening'. */
  listening: boolean;
  /** Whether the browser supports Web Speech API. */
  supported: boolean;
  /** Last error message, or null. */
  error: string | null;
  /** Clear transcript + error state for a fresh session. */
  reset: () => void;
}

interface UseSpeechRecognitionOptions {
  /** BCP-47 language code. Defaults to 'en-US'. */
  lang?: string;
  /** Auto-stop after this many ms of silence. Defaults to 2500 (2.5s). */
  silenceTimeout?: number;
  /** Called with the final transcript when speech ends. */
  onResult?: (transcript: string) => void;
  /** Called on any recognition error. */
  onError?: (error: string) => void;
}

export function useSpeechRecognition(options: UseSpeechRecognitionOptions = {}): UseSpeechRecognitionReturn {
  const { lang = 'en-US', silenceTimeout = 2500, onResult, onError } = options;

  const SpeechRecognitionClass = getSpeechRecognition();
  const supported = !!SpeechRecognitionClass;

  const [status, setStatus] = useState<VoiceStatus>('idle');
  const [transcript, setTranscript] = useState('');
  const [interimTranscript, setInterimTranscript] = useState('');
  const [error, setError] = useState<string | null>(null);

  const recognitionRef = useRef<any>(null);
  const silenceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onResultRef = useRef(onResult);
  const onErrorRef = useRef(onError);

  // Keep callback refs fresh without causing re-renders
  useEffect(() => { onResultRef.current = onResult; }, [onResult]);
  useEffect(() => { onErrorRef.current = onError; }, [onError]);

  const clearSilenceTimer = useCallback(() => {
    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
  }, []);

  const resetSilenceTimer = useCallback(() => {
    clearSilenceTimer();
    silenceTimerRef.current = setTimeout(() => {
      // Auto-stop after silence
      recognitionRef.current?.stop();
    }, silenceTimeout);
  }, [clearSilenceTimer, silenceTimeout]);

  const start = useCallback(() => {
    if (!SpeechRecognitionClass) {
      setError('Speech recognition is not supported in this browser. Please use Chrome or Edge.');
      onErrorRef.current?.('unsupported');
      return;
    }
    if (recognitionRef.current) return; // Already active

    setError(null);
    setTranscript('');
    setInterimTranscript('');

    const recognition = new SpeechRecognitionClass();
    recognition.lang = lang;
    recognition.interimResults = true;
    recognition.continuous = false; // Single utterance — stops after first final result for speed
    recognition.maxAlternatives = 1;

    recognition.onstart = () => {
      setStatus('listening');
      resetSilenceTimer();
    };

    recognition.onresult = (event: any) => {
      resetSilenceTimer();
      let finalText = '';
      let interimText = '';

      for (let i = 0; i < event.results.length; i++) {
        const result = event.results[i];
        if (result.isFinal) {
          finalText += result[0].transcript;
        } else {
          interimText += result[0].transcript;
        }
      }

      if (finalText) {
        setTranscript(finalText);
        setInterimTranscript('');
        // Fire immediately — don't wait for onend + silence timer.
        clearSilenceTimer();
        setStatus('processing');
        onResultRef.current?.(finalText.trim());
        // Stop the recognizer now that we have a final result.
        try { recognition.stop(); } catch { /* already stopped */ }
        recognitionRef.current = null;
        return;
      }
      setInterimTranscript(interimText);
    };

    recognition.onerror = (event: any) => {
      clearSilenceTimer();
      const code = event.error as string;

      // 'no-speech' and 'aborted' are normal — don't surface as errors.
      if (code === 'no-speech' || code === 'aborted') {
        setStatus('idle');
        recognitionRef.current = null;
        return;
      }

      const messages: Record<string, string> = {
        'not-allowed': 'Microphone permission denied. Please allow access in your browser settings.',
        'audio-capture': 'No microphone found. Please connect a microphone.',
        'network': 'Network error during speech recognition. Check your connection.',
        'service-not-allowed': 'Speech recognition service unavailable.',
      };
      const msg = messages[code] ?? `Speech recognition error: ${code}`;
      setError(msg);
      setStatus('idle');
      onErrorRef.current?.(msg);
      recognitionRef.current = null;
    };

    recognition.onend = () => {
      clearSilenceTimer();
      setInterimTranscript('');
      // If we already fired in onresult, recognitionRef is null — nothing to do.
      if (!recognitionRef.current) return;
      recognitionRef.current = null;
      // No final result was captured — user was silent or recognition timed out.
      setStatus('idle');
    };

    recognitionRef.current = recognition;
    try {
      recognition.start();
    } catch (e) {
      setError('Failed to start speech recognition.');
      recognitionRef.current = null;
    }
  }, [SpeechRecognitionClass, lang, resetSilenceTimer, clearSilenceTimer]);

  const stop = useCallback(() => {
    clearSilenceTimer();
    if (recognitionRef.current) {
      recognitionRef.current.stop();
    }
  }, [clearSilenceTimer]);

  const toggle = useCallback(() => {
    if (status === 'listening') {
      stop();
    } else if (status === 'idle') {
      start();
    }
    // If 'processing', do nothing — wait for it to finish.
  }, [status, start, stop]);

  const reset = useCallback(() => {
    stop();
    setTranscript('');
    setInterimTranscript('');
    setError(null);
    setStatus('idle');
  }, [stop]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearSilenceTimer();
      recognitionRef.current?.abort?.();
      recognitionRef.current = null;
    };
  }, [clearSilenceTimer]);

  return {
    start,
    stop,
    toggle,
    transcript,
    interimTranscript,
    status,
    listening: status === 'listening',
    supported,
    error,
    reset,
  };
}
